import React, { useMemo, useCallback } from "react";
import type { ProviderChatThread } from "./types";
import { ChatThreadView } from "./ChatThreadView";
import { useRouter, useSearch } from "@tanstack/react-router";
import { ChatThreadList } from "./ChatThreadList";
import { Button } from "@/components/ui/button";
const Chat = ({
  user,
  chatThreads,
  onRefreshThreads,
  organizationBusinessUnitId,
  canManageJobPosts,
  documentSigningEnabled,
  onLoadMore,
  hasMore,
  isLoading,
  isLoadingSelectedThread,
  searchQuery,
  onSearchChange,
  assignedToMe,
  onAssignedToMeChange,
  unreadOnly,
  onUnreadOnlyChange,
  isPoolRestricted,
}: {
  user:
    | {
        id: string;
        is_admin?: boolean;
      }
    | undefined;
  chatThreads: ProviderChatThread[];
  onRefreshThreads?: () => Promise<void>;
  organizationBusinessUnitId?: string;
  /** Org-side only — gates the "Send a form" composer entry and the draft resume banner. */
  canManageJobPosts?: boolean;
  /** Org-side BU feature flag — required alongside the permission for form-signing UI. */
  documentSigningEnabled?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  isLoadingSelectedThread?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  assignedToMe?: boolean;
  onAssignedToMeChange?: (value: boolean) => void;
  unreadOnly?: boolean;
  onUnreadOnlyChange?: (value: boolean) => void;
  isPoolRestricted?: boolean;
}) => {
  const router = useRouter();
  const search = useSearch({
    strict: false,
  });

  const selectedThread = useMemo(() => {
    if (!search.thread) {
      return null;
    }
    return chatThreads.find((thread) => thread.id === search.thread) ?? null;
  }, [chatThreads, search.thread]);

  const threadNotFound = Boolean(
    search.thread && !selectedThread && !isLoadingSelectedThread,
  );

  const handleUnarchive = async () => {
    await onRefreshThreads?.();
    router.navigate({
      // @ts-expect-error - existing dynamic route typing
      search: (prev) => ({
        ...prev,
        archived: false,
      }),
    });
  };

  const handleSelectThread = useCallback(
    (thread: ProviderChatThread) => {
      router.navigate({
        // @ts-expect-error - existing dynamic route typing
        search: (prev) => ({
          ...prev,
          thread: thread.id,
        }),
      });
    },
    [router],
  );

  const handleBack = useCallback(() => {
    router.navigate({
      // @ts-expect-error - existing dynamic route typing
      search: (prev) => {
        const { thread, ...rest } = prev;
        return rest;
      },
    });
  }, [router]);

  return (
    <div className="absolute inset-0 flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-slate-50 lg:flex-row">
      <div
        className={`h-full min-h-0 min-w-0 shrink-0 border-slate-200 bg-white lg:w-80 lg:border-r xl:w-96 ${selectedThread || threadNotFound ? "hidden lg:block" : ""}`}
      >
        <ChatThreadList
          threads={chatThreads}
          onSelectThread={handleSelectThread}
          selectedThread={selectedThread}
          user={user}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          searchQuery={searchQuery}
          onSearchChange={onSearchChange}
          assignedToMe={assignedToMe}
          onAssignedToMeChange={onAssignedToMeChange}
          unreadOnly={unreadOnly}
          onUnreadOnlyChange={onUnreadOnlyChange}
          isPoolRestricted={isPoolRestricted}
        />
      </div>
      {selectedThread ? (
        <ChatThreadView
          thread={selectedThread}
          onBack={handleBack}
          onUnarchive={handleUnarchive}
          onRefreshThreads={onRefreshThreads}
          currentUser={user}
          organizationBusinessUnitId={organizationBusinessUnitId}
          canManageJobPosts={canManageJobPosts}
          documentSigningEnabled={documentSigningEnabled}
        />
      ) : threadNotFound ? (
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
          <div className="text-center max-w-sm mx-auto">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <svg
                className="w-6 h-6 sm:w-8 sm:h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-2">
              Chat thread not found
            </h3>
            <p className="text-sm sm:text-base text-slate-600 mb-4 sm:mb-6 leading-relaxed">
              The conversation you're looking for doesn't exist or may have been
              deleted.
            </p>
            <Button onClick={handleBack} className="w-full sm:w-auto">
              Back to conversations
            </Button>
          </div>
        </div>
      ) : (
        <div className="hidden lg:flex min-h-0 min-w-0 flex-1 items-center justify-center">
          <div className="max-w-sm text-center">
            <div className="w-16 h-16 bg-primary/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-slate-900">
              No conversation selected
            </h3>
            <p className="mt-2 text-slate-600">
              Choose a conversation from the list to start chatting
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
